import { Panel } from './Panel'
import { useState } from 'react'
import { mutate } from '@/runtime'
import { useAllTasks, useEdgesTo } from '@/db/hooks'
import type { PageResource } from '@/kinds/page/apply'
import type { TaskResource } from '@/kinds/task/apply'
import { isOpen } from '@/kinds/task/schema'
import { useUi } from '@/state/ui'
import { Icon } from '@/lib/icons'
import { TaskRow } from '@/components/TaskRow'
import { Button } from '@/components/ui'

/** Tasks that reference this page. Link an existing one or create one here. */
export const LinkedTasks = ({ page }: { page: PageResource }) => {
  const edges = useEdgesTo(page.id).filter((e) => e.relation === 'references')
  const all = (useAllTasks() ?? []).filter((t) => !t.deleted_at)
  const linked = edges.map((e) => all.find((t) => t.id === e.from_id)).filter((t): t is TaskResource => !!t)
  const [linking, setLinking] = useState(false)
  const [q, setQ] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const flash = useUi((s) => s.showFlash)
  const linkedIds = new Set(linked.map((t) => t.id))
  const needle = q.trim().toLowerCase()
  const candidates = all
    .filter((t) => t.labels.ws === page.labels.ws && !linkedIds.has(t.id) && isOpen(t.status.state))
    .filter((t) => !needle || t.spec.title.toLowerCase().includes(needle))
    .slice(0, 8)
  const link = async (id: string) => {
    await mutate.setEdge(id, page.id, 'references')
    setQ('')
  }
  const createAndLink = async () => {
    const title = newTitle.trim()
    if (!title) return
    const t = await mutate.create('Task', { title }, page.labels.ws ? { ws: page.labels.ws } : {})
    await mutate.setEdge(t.id, page.id, 'references')
    setNewTitle('')
    flash(`Added “${title}” to the inbox`)
  }
  const open = linked.filter((t) => isOpen(t.status.state))
  const done = linked.filter((t) => !isOpen(t.status.state))
  return (
    <Panel
      title="Linked tasks"
      right={
        <Button variant="chip" onClick={() => setLinking((l) => !l)} data-testid="page-link-task">
          {linking ? 'Done' : '+ Link'}
        </Button>
      }
    >
      {linked.length === 0 && !linking && (
        <p className="text-xs text-faint">No tasks yet. Link one, or add one here.</p>
      )}
      {linked.length > 0 && (
        <ul className="flex flex-col gap-1.5" data-testid="page-linked-tasks">
          {[...open, ...done].map((t) => (
            <li key={t.id} className="relative">
              <ul className="group-list">
                <TaskRow task={t} mode="compact" />
              </ul>
              {linking && (
                <button
                  type="button"
                  onClick={() => void mutate.setEdge(t.id, page.id, 'references', true)}
                  className="absolute right-1 top-1 h-7 w-7 rounded-md bg-panel text-faint hover:text-rose-300"
                  aria-label={`Unlink ${t.spec.title}`}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {linking && (
        <div className="mt-2 flex flex-col gap-2 fade-in">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void createAndLink()
              }
            }}
            placeholder="New task from this page ⏎"
            className="w-full text-sm min-h-8 py-1"
            aria-label="New linked task"
            data-testid="page-new-task"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Link an existing task…"
            className="w-full text-sm min-h-8 py-1"
            aria-label="Find a task to link"
          />
          {candidates.length > 0 && (
            <ul className="flex flex-col">
              {candidates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => void link(t.id)}
                    className="w-full text-left text-sm px-2 min-h-8 rounded hover:bg-raised truncate"
                  >
                    <Icon value={t.spec.icon} className="mr-1" />
                    {t.spec.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Panel>
  )
}
